import { LifespanDeltas, RxLifespanDto, RxWithTherapistsDto, TherapistInfo, PatientInfo } from "../../../api-client";
import React from "react";

interface ColumnNameConfig<T> {
  header: string;
  field: keyof T; 
  align?: "left" | "right" | "center";
  format?: (val: T[keyof T]) => string | number;
  csvFormat?: (val: T[keyof T]) => string;
  width?: string | number;
  tooltip?: string;
  key?: string; // Optional unique identifier for the column
  relatedToKeys?: string[]; // Optional: which keys this column is related to for highlighting
};

// Type definitions for transformed data
type TransformedRxWithTherapistsDto = RxWithTherapistsDto & { clickableRxNumber: JSX.Element };
type TransformedRxLifespanDto = RxLifespanDto & { clickableRxNumber: JSX.Element };

interface PatientCumulativeData {
  locationId: string, 
  locationName: string, 
  date: Date, 
  cumulativeSum: number
}
  
interface PatientCountData {
  locationId: string, 
  locationName: string, 
  date: Date, 
  newPatients: number
}

interface TotalRxCountData {
  locationId: string, 
  locationName: string,
  date: Date, 
  totalCount: number
}

interface RxStatusCountData {
  locationId: string, 
  locationName: string,
  date: Date, 
  ongoingRxCount: number, 
  settledRxCount: number
}

interface TotalRxValueData {
  locationId: string, 
  locationName: string,
  date: Date, 
  // totalOngoingRxPrice: number, 
  // totalSettledRxPrice: number
  totalRxValue: number,
}

interface TotalApptValueData {
  locationId: string, 
  locationName: string,
  date: Date, 
  totalValue: number
}

interface TotalGapsData {
  locationId: string, 
  locationName: string,
  date: Date, 
  totalGaps: number
}

interface TherapyRxFrequencyData {
  locationId: string,
  frequency: number,
  count: number,
  percentage: number,
}
interface EstimatedMonthlyRevenueData {
  id: number, 
  date: string, 
  locationId: string, 
  monthlyRevenue: number
}

export const plotDataTableConfigs = {
  cumulativePatients: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(val) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Kumulative Anzahl', field: 'cumulativeSum', align: "right", format: (val: number) => new Intl.NumberFormat('de-DE').format(val), csvFormat: (val: number) => val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2}) },
    ] as ColumnNameConfig<PatientCumulativeData>[],
  },

  newPatients: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(val) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Anzahl neuer Patienten', field: 'newPatients', align: "right", format: (val: number) => new Intl.NumberFormat('de-DE').format(val) },
    ] as ColumnNameConfig<PatientCountData>[],
  },

  totalRxCount: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(val) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Rx Anzahl', field: 'totalCount', align: "right", format: (val: number) => new Intl.NumberFormat('de-DE').format(val) },
    ] as ColumnNameConfig<TotalRxCountData>[],
  },

  totalRxValue: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(val) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Gesamtwert der Verordnungen €', field: 'totalRxValue', align: "right", format: (val: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(val), csvFormat: (val: number) => val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2}) },
    ] as ColumnNameConfig<TotalRxValueData>[],
  }, 

  rxTherapistCount: {
    columns: [
    { header: 'Rx #', field: 'clickableRxNumber', align: "left" },
    { header: 'Patient', field: 'patient', align: "left", format: (val: PatientInfo) => `${val.firstName} ${val.lastName}` },
    { header: 'Behandlungsanzahl', field: 'therapyRxAmounts', align: "right", format: (val: { amount: number }[]) => val.map(tra => `${tra.amount}`).join(", ") },
    { header: 'Therapeuten', field: 'therapists', align: "left", format: (val: TherapistInfo[]) => val.map(t => `${t.firstName} ${t.lastName}`.trim()).join(", "), width: 250 },
    { header: 'Standort', field: 'locationId', align: "left" },
    ] as ColumnNameConfig<TransformedRxWithTherapistsDto>[],
    downloadColumns: [
    { header: 'Rx #', field: 'rxNumber', align: "left", csvFormat: (val: string) => `${val}` + "\u00A0" },
    { header: 'Patient', field: 'patient', align: "left", format: (val: PatientInfo) => `${val.firstName} ${val.lastName}` },
    { header: 'Behandlungsanzahl', field: 'therapyRxAmounts', align: "right", format: (val: { amount: number }[]) => val.map(tra => `${tra.amount}`).join(", ") },
    { header: 'Therapeuten', field: 'therapists', align: "left", format: (val: TherapistInfo[]) => val.map(t => `${t.firstName} ${t.lastName}`.trim()).join(", "), width: 250 },
    { header: 'Standort', field: 'locationId', align: "left" },
    ] as ColumnNameConfig<RxWithTherapistsDto>[],
  },

  rxLifespan: {
    columns: [
      { header: 'Rx #', field: 'clickableRxNumber', align: "left" },
      { header: 'Patient', field: 'patient', align: "left", format: (val: PatientInfo) => `${val.firstName} ${val.lastName}` },
      { header: "Standort", field: "locationId", align: "left" },
      { header: "Rx Datum", field: "rxCreatedAt", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "rxDateToBooking" },
      { header: "Erstbehandlung", field: "firstTreatmentDate", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "firstTreatmentToBooking" },
      { header: "Letzte Behandlung", field: "lastTreatmentDate", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "lastTreatmentToBooking" },
      { header: "Abrechnung", field: "insuranceCreatedAt", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), relatedToKeys: ["rxDateToBooking", "firstTreatmentToBooking", "lastTreatmentToBooking"] },
      { header: "Rx → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.rxDateToBooking), width: 100, tooltip: "Rezeptdatum bis zur Abrechnung (Tage)", key: "rxDateToBooking" },
      { header: "Erst → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.firstTreatmentToBooking), width: 100, tooltip: "Erstbehandlung bis zur Abrechnung (Tage)", key: "firstTreatmentToBooking" },
      { header: "Letzte → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.lastTreatmentToBooking), width: 100, tooltip: "Letzte Behandlung bis zur Abrechnung (Tage)", key: "lastTreatmentToBooking" },
    ] as ColumnNameConfig<TransformedRxLifespanDto>[],
    downloadColumns: [
      { header: 'Rx #', field: 'rxNumber', align: "left", csvFormat: (val: string) => `${val}` + "\u00A0" },
      { header: 'Patient', field: 'patient', align: "left", format: (val: PatientInfo) => `${val.firstName} ${val.lastName}` },
      { header: "Standort", field: "locationId", align: "left" },
      { header: "Rx Datum", field: "rxCreatedAt", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "rxDateToBooking" },
      { header: "Erstbehandlung", field: "firstTreatmentDate", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "firstTreatmentToBooking" },
      { header: "Letzte Behandlung", field: "lastTreatmentDate", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "lastTreatmentToBooking" },
      { header: "Abrechnung", field: "insuranceCreatedAt", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "rxDateToBooking,firstTreatmentToBooking,lastTreatmentToBooking" },
      { header: "Rx → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.rxDateToBooking), width: 100, tooltip: "Rezeptdatum bis zur Abrechnung (Tage)", key: "rxDateToBooking" },
      { header: "Erst → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.firstTreatmentToBooking), width: 100, tooltip: "Erstbehandlung bis zur Abrechnung (Tage)", key: "firstTreatmentToBooking" },
      { header: "Letzte → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.lastTreatmentToBooking), width: 100, tooltip: "Letzte Behandlung bis zur Abrechnung (Tage)", key: "lastTreatmentToBooking" },
    ] as ColumnNameConfig<RxLifespanDto>[],
  },

  totalApptValue: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(val) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Gesamtwert der Termine €', field: 'totalValue', align: "right", format: (val: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(val), csvFormat: (val: number) => val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2}) }
    ] as ColumnNameConfig<TotalApptValueData>[],
  }, 

  totalGaps: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(val) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Gesamte Lücken', field: 'totalGaps', align: "right", format: (val: number) => new Intl.NumberFormat('de-DE').format(val) }
    ] as ColumnNameConfig<TotalGapsData>[],
  },

  therapyRxFrequency: {
    columns: [
      { header: "Standort", field: "locationId", align: "left" },
      { header: "Auftretenshäufigkeit", field: "frequency", align: "center", width: 150 },
      { header: "Anzahl", field: "count", align: "right", format: (val: number) => new Intl.NumberFormat('de-DE').format(val) },
      { header: "%", field: "percentage", align: "right", format: (val: number) => new Intl.NumberFormat("de-DE", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val), csvFormat: (val: number) => (val * 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2}) + "%" },
    ] as ColumnNameConfig<TherapyRxFrequencyData>[],
  },

  estimatedMonthlyRevenue: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date | string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Geschätzter monatlicher Umsatz €', field: 'monthlyRevenue', align: "right", format: (val: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(val), csvFormat: (val: number) => val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2}) },
    ] as ColumnNameConfig<EstimatedMonthlyRevenueData>[],
  },

  totalRxBlankoCount: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(val) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Rx Anzahl', field: 'totalCount', align: "right", format: (val: number) => new Intl.NumberFormat('de-DE').format(val) },
    ] as ColumnNameConfig<TotalRxCountData>[],
  },

  totalRxBlankoValue: {
    columns: [
      { header: 'Datum', field: 'date', align: "left", format: (val: Date) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(val) },
      { header: 'Standort', field: 'locationId', align: "left" },
      { header: 'Gesamtwert der Verordnungen €', field: 'totalRxValue', align: "right", format: (val: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(val), csvFormat: (val: number) => val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2}) },
    ] as ColumnNameConfig<TotalRxValueData>[],
  }, 

  rxBlankoTherapistCount: {
    columns: [
    { header: 'Rx #', field: 'clickableRxNumber', align: "left" },
    { header: 'Patient', field: 'patient', align: "left", format: (val: PatientInfo) => `${val.firstName} ${val.lastName}` },
    { header: 'Behandlungsanzahl', field: 'therapyRxAmounts', align: "right", format: (val: { amount: number }[]) => val.map(tra => `${tra.amount}`).join(", ") },
    { header: 'Therapeuten', field: 'therapists', align: "left", format: (val: TherapistInfo[]) => val.map(t => `${t.firstName} ${t.lastName}`.trim()).join(", "), width: 250 },
    { header: 'Standort', field: 'locationId', align: "left" },
    ] as ColumnNameConfig<TransformedRxWithTherapistsDto>[],
    downloadColumns: [
    { header: 'Rx #', field: 'rxNumber', align: "left", csvFormat: (val: string) => `${val}` + "\u00A0" },
    { header: 'Patient', field: 'patient', align: "left", format: (val: PatientInfo) => `${val.firstName} ${val.lastName}` },
    { header: 'Behandlungsanzahl', field: 'therapyRxAmounts', align: "right", format: (val: { amount: number }[]) => val.map(tra => `${tra.amount}`).join(", ") },
    { header: 'Therapeuten', field: 'therapists', align: "left", format: (val: TherapistInfo[]) => val.map(t => `${t.firstName} ${t.lastName}`.trim()).join(", "), width: 250 },
    { header: 'Standort', field: 'locationId', align: "left" },
    ] as ColumnNameConfig<RxWithTherapistsDto>[],
  },

  rxBlankoLifespan: {
    columns: [
      { header: 'Rx #', field: 'clickableRxNumber', align: "left" },
      { header: 'Patient', field: 'patient', align: "left", format: (val: PatientInfo) => `${val.firstName} ${val.lastName}` },
      { header: "Standort", field: "locationId", align: "left" },
      { header: "Rx Datum", field: "rxCreatedAt", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "rxDateToBooking" },
      { header: "Erstbehandlung", field: "firstTreatmentDate", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "firstTreatmentToBooking" },
      { header: "Letzte Behandlung", field: "lastTreatmentDate", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "lastTreatmentToBooking" },
      { header: "Abrechnung", field: "insuranceCreatedAt", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), relatedToKeys: ["rxDateToBooking", "firstTreatmentToBooking", "lastTreatmentToBooking"] },
      { header: "Rx → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.rxDateToBooking), width: 100, tooltip: "Rezeptdatum bis zur Abrechnung (Tage)", key: "rxDateToBooking" },
      { header: "Erst → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.firstTreatmentToBooking), width: 100, tooltip: "Erstbehandlung bis zur Abrechnung (Tage)", key: "firstTreatmentToBooking" },
      { header: "Letzte → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.lastTreatmentToBooking), width: 100, tooltip: "Letzte Behandlung bis zur Abrechnung (Tage)", key: "lastTreatmentToBooking" },
    ] as ColumnNameConfig<TransformedRxLifespanDto>[],
    downloadColumns: [
      { header: 'Rx #', field: 'rxNumber', align: "left", csvFormat: (val: string) => `${val}` + "\u00A0" },
      { header: 'Patient', field: 'patient', align: "left", format: (val: PatientInfo) => `${val.firstName} ${val.lastName}` },
      { header: "Standort", field: "locationId", align: "left" },
      { header: "Rx Datum", field: "rxCreatedAt", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "rxDateToBooking" },
      { header: "Erstbehandlung", field: "firstTreatmentDate", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "firstTreatmentToBooking" },
      { header: "Letzte Behandlung", field: "lastTreatmentDate", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "lastTreatmentToBooking" },
      { header: "Abrechnung", field: "insuranceCreatedAt", align: "left", format: (val: string) => new Intl.DateTimeFormat('de-DE', { year: "numeric", month: "short", day: "numeric" }).format(new Date(val)), key: "rxDateToBooking,firstTreatmentToBooking,lastTreatmentToBooking" },
      { header: "Rx → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.rxDateToBooking), width: 100, tooltip: "Rezeptdatum bis zur Abrechnung (Tage)", key: "rxDateToBooking" },
      { header: "Erst → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.firstTreatmentToBooking), width: 100, tooltip: "Erstbehandlung bis zur Abrechnung (Tage)", key: "firstTreatmentToBooking" },
      { header: "Letzte → Abr.", field: "lifespanDeltas", align: "right", format: (val: LifespanDeltas) => new Intl.NumberFormat('de-DE').format(val.lastTreatmentToBooking), width: 100, tooltip: "Letzte Behandlung bis zur Abrechnung (Tage)", key: "lastTreatmentToBooking" },
    ] as ColumnNameConfig<RxLifespanDto>[],
  },
}